import React, { useMemo, useState } from 'react';
import {
  FiArrowDown,
  FiArrowUp,
  FiCheck,
  FiPlus,
  FiRepeat,
  FiTrash2,
} from 'react-icons/fi';
import './TourFlowBuilder.css';

const MAX_PREVIEW_PRESETS = 36;
const MAX_STEP_REPEAT = 1000;

const normalizeRepeat = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 1;
  }
  return Math.max(1, Math.min(MAX_STEP_REPEAT, Math.trunc(parsed)));
};

const getPresetId = (preset) => {
  const rawValue = preset?.backendId ?? preset?.backend_id ?? preset?.id;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const comparePresetOptions = (left, right) => {
  if (left.id !== right.id) {
    return left.id - right.id;
  }
  return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
};

const normalizeGroupPresetIds = (presetIds) => {
  if (!Array.isArray(presetIds)) {
    return [];
  }

  return [...new Set(
    presetIds
      .map((presetId) => Number(presetId))
      .filter((presetId) => Number.isFinite(presetId)),
  )];
};

const getStepPresetIds = (step, groups) => {
  if (step?.group && Array.isArray(groups?.[step.group])) {
    return normalizeGroupPresetIds(groups[step.group]);
  }
  return normalizeGroupPresetIds(step?.presets);
};

const expandSequencePreview = (steps, groups) => {
  const route = [];
  let totalVisits = 0;

  (Array.isArray(steps) ? steps : []).forEach((step) => {
    const presetIds = getStepPresetIds(step, groups);
    const repeat = normalizeRepeat(step?.repeat);
    totalVisits += presetIds.length * repeat;

    for (let repeatIndex = 0; repeatIndex < repeat && route.length < MAX_PREVIEW_PRESETS; repeatIndex += 1) {
      presetIds.forEach((presetId) => {
        if (route.length < MAX_PREVIEW_PRESETS) {
          route.push(presetId);
        }
      });
    }
  });

  return { route, totalVisits };
};

function SequenceEditor({ label, tone, steps, groups, onChange }) {
  const groupNames = Object.keys(groups || {});
  const safeSteps = useMemo(
    () => (Array.isArray(steps) ? steps : []),
    [steps],
  );
  const preview = useMemo(
    () => expandSequencePreview(safeSteps, groups),
    [groups, safeSteps],
  );

  const updateStep = (stepIndex, patch) => {
    onChange(safeSteps.map((step, index) => (
      index === stepIndex ? { ...step, ...patch } : step
    )));
  };

  const moveStep = (stepIndex, direction) => {
    const targetIndex = stepIndex + direction;
    if (targetIndex < 0 || targetIndex >= safeSteps.length) {
      return;
    }

    const nextSteps = [...safeSteps];
    [nextSteps[stepIndex], nextSteps[targetIndex]] = [nextSteps[targetIndex], nextSteps[stepIndex]];
    onChange(nextSteps);
  };

  const addStep = () => {
    if (groupNames.length === 0) {
      return;
    }
    onChange([...safeSteps, { group: groupNames[0], repeat: 1 }]);
  };

  return (
    <section className={`tourFlowSequence tourFlowSequence--${tone}`}>
      <div className="tourFlowSequence__header">
        <div>
          <span className="tourFlowSequence__eyebrow">{label} route</span>
          <strong>{safeSteps.length} step{safeSteps.length === 1 ? '' : 's'}</strong>
        </div>
        <span className="tourFlowSequence__visits">
          {preview.totalVisits} visit{preview.totalVisits === 1 ? '' : 's'} / loop
        </span>
      </div>

      {safeSteps.length === 0 ? (
        <div className="tourFlowSequence__empty">
          <FiRepeat />
          <span>No custom steps yet. The simple preset list remains active.</span>
        </div>
      ) : (
        <div className="tourFlowSequence__steps">
          {safeSteps.map((step, stepIndex) => {
            const isInlineStep = !step?.group && Array.isArray(step?.presets);
            const missingGroup = Boolean(step?.group && !groups?.[step.group]);
            const selectValue = isInlineStep ? `__inline_${stepIndex}` : (step?.group || '');

            return (
              <div className="tourFlowStep" key={`${tone}-${stepIndex}`}>
                <span className="tourFlowStep__number">{stepIndex + 1}</span>
                <label className="tourFlowStep__field">
                  <span>Group</span>
                  <select
                    className={`settingsInput${missingGroup ? ' tourFlowStep__input--error' : ''}`}
                    value={selectValue}
                    onChange={(event) => updateStep(stepIndex, {
                      group: event.target.value,
                      presets: undefined,
                    })}
                  >
                    {isInlineStep ? (
                      <option value={`__inline_${stepIndex}`} disabled>
                        Inline: {normalizeGroupPresetIds(step.presets).join(', ')}
                      </option>
                    ) : null}
                    {missingGroup ? (
                      <option value={step.group}>Missing: {step.group}</option>
                    ) : null}
                    {groupNames.map((groupName) => (
                      <option key={groupName} value={groupName}>{groupName}</option>
                    ))}
                  </select>
                </label>

                <label className="tourFlowStep__field tourFlowStep__field--repeat">
                  <span>Repeat</span>
                  <div className="tourFlowStep__repeatInput">
                    <input
                      className="settingsInput"
                      type="number"
                      min="1"
                      max={MAX_STEP_REPEAT}
                      step="1"
                      value={normalizeRepeat(step?.repeat)}
                      onChange={(event) => updateStep(stepIndex, {
                        repeat: normalizeRepeat(event.target.value),
                      })}
                      aria-label={`${label} step ${stepIndex + 1} repeat count`}
                    />
                    <span>×</span>
                  </div>
                </label>

                <div className="tourFlowStep__actions">
                  <button
                    type="button"
                    className="tourFlowIconButton"
                    onClick={() => moveStep(stepIndex, -1)}
                    disabled={stepIndex === 0}
                    aria-label={`Move ${label} step ${stepIndex + 1} up`}
                    title="Move up"
                  >
                    <FiArrowUp />
                  </button>
                  <button
                    type="button"
                    className="tourFlowIconButton"
                    onClick={() => moveStep(stepIndex, 1)}
                    disabled={stepIndex === safeSteps.length - 1}
                    aria-label={`Move ${label} step ${stepIndex + 1} down`}
                    title="Move down"
                  >
                    <FiArrowDown />
                  </button>
                  <button
                    type="button"
                    className="tourFlowIconButton tourFlowIconButton--danger"
                    onClick={() => onChange(safeSteps.filter((_, index) => index !== stepIndex))}
                    aria-label={`Remove ${label} step ${stepIndex + 1}`}
                    title="Remove step"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className="settingsButton settingsButton--ghost tourFlowSequence__add"
        onClick={addStep}
        disabled={groupNames.length === 0}
      >
        <FiPlus />
        <span>Add {label.toLowerCase()} step</span>
      </button>

      {preview.route.length > 0 ? (
        <div className="tourFlowSequence__preview">
          <span>Preview</span>
          <p>
            {preview.route.join(' → ')}
            {preview.totalVisits > preview.route.length ? ' → …' : ''}
            {' → repeat'}
          </p>
        </div>
      ) : null}
    </section>
  );
}

export default function TourFlowBuilder({
  presetGroups = {},
  daySequence = [],
  nightSequence = [],
  presets = [],
  onChange,
}) {
  const [newGroupName, setNewGroupName] = useState('');
  const [groupError, setGroupError] = useState('');
  const groups = presetGroups && typeof presetGroups === 'object' && !Array.isArray(presetGroups)
    ? presetGroups
    : {};
  const groupEntries = Object.entries(groups);
  const hasCustomFlow = groupEntries.length > 0
    || daySequence.length > 0
    || nightSequence.length > 0;
  const presetOptions = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(presets) ? presets : [])
      .map((preset) => {
        const id = getPresetId(preset);
        const name = String(preset?.name || '').trim();
        return id === null ? null : {
          id,
          label: name ? `${id} · ${name}` : String(id),
        };
      })
      .filter((option) => {
        if (!option || seen.has(option.id)) {
          return false;
        }
        seen.add(option.id);
        return true;
      })
      .sort(comparePresetOptions);
  }, [presets]);

  const emitChange = (patch) => {
    onChange?.({
      presetGroups: groups,
      daySequence,
      nightSequence,
      ...patch,
    });
  };

  const addGroup = () => {
    const groupName = newGroupName.trim();
    if (!groupName) {
      setGroupError('Give the group a short name first.');
      return;
    }
    if (Object.prototype.hasOwnProperty.call(groups, groupName)) {
      setGroupError(`A group called “${groupName}” already exists.`);
      return;
    }

    emitChange({ presetGroups: { ...groups, [groupName]: [] } });
    setNewGroupName('');
    setGroupError('');
  };

  const renameGroup = (previousName, nextRawName, input) => {
    const nextName = nextRawName.trim();
    if (!nextName || nextName === previousName) {
      input.value = previousName;
      return;
    }
    if (Object.prototype.hasOwnProperty.call(groups, nextName)) {
      setGroupError(`A group called “${nextName}” already exists.`);
      input.value = previousName;
      return;
    }

    const nextGroups = Object.entries(groups).reduce((acc, [groupName, presetIds]) => {
      acc[groupName === previousName ? nextName : groupName] = presetIds;
      return acc;
    }, {});
    const updateSequenceGroupName = (steps) => steps.map((step) => (
      step?.group === previousName ? { ...step, group: nextName } : step
    ));
    emitChange({
      presetGroups: nextGroups,
      daySequence: updateSequenceGroupName(daySequence),
      nightSequence: updateSequenceGroupName(nightSequence),
    });
    setGroupError('');
  };

  const removeGroup = (groupName) => {
    const usedCount = [...daySequence, ...nightSequence]
      .filter((step) => step?.group === groupName)
      .length;
    const message = usedCount > 0
      ? `Remove “${groupName}” and its ${usedCount} flow step${usedCount === 1 ? '' : 's'}?`
      : `Remove the “${groupName}” group?`;
    if (!window.confirm(message)) {
      return;
    }

    const nextGroups = { ...groups };
    delete nextGroups[groupName];
    emitChange({
      presetGroups: nextGroups,
      daySequence: daySequence.filter((step) => step?.group !== groupName),
      nightSequence: nightSequence.filter((step) => step?.group !== groupName),
    });
  };

  const toggleGroupPreset = (groupName, presetId) => {
    const activePresetIds = normalizeGroupPresetIds(groups[groupName]);
    const nextPresetIds = activePresetIds.includes(presetId)
      ? activePresetIds.filter((activeId) => activeId !== presetId)
      : [...activePresetIds, presetId];

    emitChange({
      presetGroups: {
        ...groups,
        [groupName]: nextPresetIds,
      },
    });
  };

  const resetToSimpleLists = () => {
    if (!window.confirm('Clear all custom groups and flow steps? The simple day and night preset lists will remain.')) {
      return;
    }
    emitChange({ presetGroups: {}, daySequence: [], nightSequence: [] });
    setGroupError('');
  };

  return (
    <div className="tourFlowBuilder">
      <div className="tourFlowBuilder__header">
        <div>
          <span className="tourFlowBuilder__eyebrow">Advanced routing</span>
          <h4>Tour flow builder</h4>
          <p>Group useful views together, then decide how often each group runs.</p>
        </div>
        <div className="tourFlowBuilder__statusRow">
          <span className={`tourFlowBuilder__status${hasCustomFlow ? ' tourFlowBuilder__status--active' : ''}`}>
            {hasCustomFlow ? 'Custom flow' : 'Simple lists'}
          </span>
          {hasCustomFlow ? (
            <button
              type="button"
              className="settingsButton settingsButton--ghost settingsButton--small"
              onClick={resetToSimpleLists}
            >
              Use simple lists
            </button>
          ) : null}
        </div>
      </div>

      <div className="tourFlowBuilder__groupsHeader">
        <div>
          <strong>1. Build preset groups</strong>
          <span>Each group runs from left to right.</span>
        </div>
        <div className="tourFlowBuilder__newGroup">
          <input
            className="settingsInput"
            value={newGroupName}
            onChange={(event) => {
              setNewGroupName(event.target.value);
              setGroupError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addGroup();
              }
            }}
            placeholder="e.g. hot_spots"
            aria-label="New preset group name"
          />
          <button type="button" className="settingsButton" onClick={addGroup}>
            <FiPlus />
            <span>Add group</span>
          </button>
        </div>
      </div>

      {groupError ? <div className="settingsInlineError">{groupError}</div> : null}

      {groupEntries.length === 0 ? (
        <div className="tourFlowBuilder__emptyGroups">
          <FiRepeat />
          <div>
            <strong>Your simple day and night lists still work.</strong>
            <span>Add a group when you want a custom repeating route.</span>
          </div>
        </div>
      ) : (
        <div className="tourFlowBuilder__groupList">
          {groupEntries.map(([groupName, presetIds], groupIndex) => {
            const activePresetIds = normalizeGroupPresetIds(presetIds);
            return (
              <article className="tourFlowGroup" key={`${groupIndex}-${groupName}`}>
                <div className="tourFlowGroup__header">
                  <label>
                    <span>Group name</span>
                    <input
                      className="settingsInput tourFlowGroup__nameInput"
                      defaultValue={groupName}
                      onBlur={(event) => renameGroup(groupName, event.target.value, event.target)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.currentTarget.blur();
                        }
                      }}
                      aria-label={`Rename ${groupName}`}
                    />
                  </label>
                  <div className="tourFlowGroup__summary">
                    <span>{activePresetIds.length} preset{activePresetIds.length === 1 ? '' : 's'}</span>
                    <button
                      type="button"
                      className="tourFlowIconButton tourFlowIconButton--danger"
                      onClick={() => removeGroup(groupName)}
                      aria-label={`Remove ${groupName}`}
                      title="Remove group"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>

                {presetOptions.length === 0 ? (
                  <div className="tourFlowGroup__noPresets">
                    Save or import camera presets first, then add them to this group.
                  </div>
                ) : (
                  <div className="tourFlowGroup__presetGrid">
                    {presetOptions.map((preset) => {
                      const isActive = activePresetIds.includes(preset.id);
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          className={`tourFlowPreset${isActive ? ' tourFlowPreset--active' : ''}`}
                          onClick={() => toggleGroupPreset(groupName, preset.id)}
                          aria-pressed={isActive}
                        >
                          <span className="tourFlowPreset__check" aria-hidden="true">
                            {isActive ? <FiCheck /> : null}
                          </span>
                          <span>{preset.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {presetOptions.length > 0 && activePresetIds.length === 0 ? (
                  <div className="tourFlowGroup__selectionHint">
                    Select at least one preset before using this group in a flow.
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      <div className="tourFlowBuilder__flowHeader">
        <strong>2. Arrange the flow</strong>
        <span>The complete route restarts automatically after its last step.</span>
      </div>

      <div className="tourFlowBuilder__sequences">
        <SequenceEditor
          label="Day"
          tone="day"
          steps={daySequence}
          groups={groups}
          onChange={(nextSequence) => emitChange({ daySequence: nextSequence })}
        />
        <SequenceEditor
          label="Night"
          tone="night"
          steps={nightSequence}
          groups={groups}
          onChange={(nextSequence) => emitChange({ nightSequence: nextSequence })}
        />
      </div>
    </div>
  );
}
